'use client';

import { useState, type SubmitEvent } from 'react';
import Button, { ButtonVariants } from '@pixum/button';
import { EmailInput } from '@pixum/web-input';
import { flattenI18n } from '../../utils/i18nConstants/flatternI18n';
import type { NewsletterFormProps } from './types';
import { ensureSession } from 'services/session/client';
import { registerNewsletterAction } from './actions';
import './styles.css';
import ThemeProvider from '@pixum/theme-provider';

/**
 * Renders and submits the shared newsletter registration form.
 *
 * @param constants - Localized form labels and validation messages.
 * @param className - Optional form class name for the consuming layout.
 * @param theme - Optional theme for the form.
 * @param successMessage - Content rendered after successful registration.
 */
export function NewsletterForm({
  constants,
  className = 'footer__newsletter__form',
  theme,
  successMessage
}: NewsletterFormProps) {
  const [emailRegistered, setEmailRegistered] = useState(false);
  const [value, setValue] = useState('');
  const [helperText, setHelperText] = useState('');
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [pending, setPending] = useState(false);

  const emailAddressConst = flattenI18n(constants.i18n_email_address);
  const registerNowConst = flattenI18n(constants.i18n_register_now);

  const handleSubmit = async (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPending(true);
    setIsEmailInvalid(false);

    try {
      // A fast submit waits for the client session: without a JWT cookie the action would register
      // the email against a throwaway anonymous session.
      await ensureSession();
    } catch {
      setIsEmailInvalid(true);
      setPending(false);

      return;
    }

    const result = await registerNewsletterAction(value);

    if (result.success) {
      setEmailRegistered(true);
    } else {
      setIsEmailInvalid(true);
      setHelperText(result.error.message);
    }

    setPending(false);
  };

  const handleNewsletterOnFocus = () => {
    setIsEmailInvalid(false);
    setHelperText('');
  };

  return (
    <ThemeProvider theme={theme}>
      {emailRegistered ? (
        successMessage
      ) : (
        <form className={className} onSubmit={handleSubmit}>
          <EmailInput
            autoComplete="email"
            hasError={isEmailInvalid}
            inputInfo={helperText}
            aria-invalid={isEmailInvalid}
            label={emailAddressConst}
            onChange={e => setValue(e.target.value)}
            onFocus={handleNewsletterOnFocus}
            value={value}
            required
          />

          {registerNowConst && (
            <Button
              isBusy={pending}
              label={registerNowConst}
              variant={ButtonVariants.PRIMARY}
              aria-busy={pending}
              type="submit"
            >
              {registerNowConst}
            </Button>
          )}
        </form>
      )}
    </ThemeProvider>
  );
}
